import React, {cloneElement, useCallback, useImperativeHandle, useRef, useState} from 'react';
import useEventListener from '../hooks/useEventListener';
import {createPortal} from 'react-dom';

function Popover({title, children, content, trigger = 'manual'}, ref) {
    const domNode = useRef();
    const [popoverBodyEl, setPopoverBodyEl] = useState(null);
    useImperativeHandle(ref, () => {
        return {
            hide: () => {
                if (popoverBodyEl) {
                    $(domNode.current).popover('hide');
                }
            },
            show: () => {
                if (!popoverBodyEl) {
                    $(domNode.current).popover('show');
                }
            },
        };
    }, [popoverBodyEl]);

    const shownHandler = useCallback(() => {
        const _popoverBodyEl = document
            .getElementById(domNode.current.getAttribute('aria-describedby'))
            ?.querySelector(':scope > .popover-content');
        if (_popoverBodyEl) {
            _popoverBodyEl.innerHTML = '';
            setPopoverBodyEl(_popoverBodyEl);
        }
    }, []);

    const hideHandler = useCallback(() => {
        setPopoverBodyEl(null);
    }, []);

    const mountContainer = useCallback((ref) => {
        if (ref === null) {
            if (domNode.current) {
                const $ref = $(domNode.current);
                if ($ref.popover) {
                    $ref.popover('destroy');
                }
                $ref
                    .off('shown.bs.popover', shownHandler)
                    .off('hide.bs.popover', hideHandler);
            }
            domNode.current = null;
        } else {
            domNode.current = ref;

            // popover plugin may not be loaded yet
            $(document).ready(function () {
                if (domNode.current !== ref) return;

                const $ref = $(ref);

                $ref.popover({
                    template: '<div class="popover"><div class="arrow"></div><div class="popover-content" style="width: 260px"></div></div>',
                    html: true,
                    sanitize: false,
                    placement: 'bottom',
                    content: 'hi', // Dummy content that will get removed. Either `content` or `title` is required.
                    animation: false, // Animations have to be off for `shown.bs.popover` to be triggered immediately
                    trigger,
                    // container: 'body',
                });

                $ref
                    .on('shown.bs.popover', shownHandler)
                    .on('hide.bs.popover', hideHandler);
            });
        }
    }, [trigger, shownHandler, hideHandler]);

    useEventListener('click', (event) => {
        if (!popoverBodyEl) {
            // Popover isnt oppened, dont care.
            return;
        }
        if (!event.target.isConnected) {
            // Some content inside the popover likely changed upon a click. Assume this shouldnt cause the popover to close.
            return;
        }
        if (domNode.current.contains(event.target)) {
            // Clicking on the button is handled by parent component
            return;
        }

        const popoverContainer = popoverBodyEl.closest('.popover');
        if (!popoverContainer.contains(event.target)) {
            // Clicking outside of the popup should close it
            $(domNode.current).popover('hide');
        }
    });

    function togglePopup() {
        if (popoverBodyEl) {
            $(domNode.current).popover('hide');
        } else {
            $(domNode.current).popover('show');
        }
    }

    return <>
        {/* afaik this is a checker bug. callback refs should be fine. */}
        {/* eslint-disable-next-line */}
        {cloneElement(children, {ref: mountContainer, onClick: trigger === 'manual' ? togglePopup : null}) }
        {popoverBodyEl
            ? createPortal(<PopoverBody title={title}>{content}</PopoverBody>, popoverBodyEl)
            : null}
    </>;
}

export default React.forwardRef(Popover);

function PopoverBody({title, children}) {
    // bootstrap's popover doesnt allow changing title dynamically. Fake it by nesting another title/content so that we have control over it.
    return <div style={{margin: '-9px -14px'}}>
        {title ? <h3 className="popover-title text-bold">{title}</h3> : null}
        <div className="popover-content">{children}</div>
    </div>
}
